import { TCommonError } from '@models/common';
import {
  TInventoryReportPdfReq,
  TInventoryReportPreviewReq,
  TInventoryReportPreviewResponse,
} from '@models/inventoryReport';
import { TAxiosResponse } from 'types';
import { axiosInstance } from './axiosInstance';

/**
 * Service layer for VAMS_PC_007 (棚卸報告書出力 — Inventory Report Output).
 * Contract: vams_docs/Tasks/PC007/api-integration.md §2.1 (preview) / §2.2 (pdf) — both ĐÃ CÓ ở BE.
 *
 * Dropdown lookups [4] 棚卸年月 (§2.3, Gap G1-a) và [5] 店舗 (§2.4, Gap G1-b) — BE CHƯA CÓ endpoint —
 * KHÔNG có hàm service tương ứng ở đây. Xem pages/InventoryReportPage/constant.ts (MOCK_PERIODS /
 * MOCK_STORES) + hook/useInventoryReportApi.tsx cho phần mock có kiểm soát.
 */
const inventoryReportApi = {
  /**
   * GET /api/v1/reports/inventory/preview — [6.2] báo cáo preview, reload mỗi khi [4]/[5] đổi.
   * Status: ĐÃ CÓ ở BE (InventoryReportController.Preview).
   *
   * CỐ Ý không gửi `sessionCode`: dropdown [4] hiện là mock (Gap G1-a), các `sessionCode` trong
   * MOCK_PERIODS không tương ứng với bản ghi t_inventory_sessions thật — theo contract §2.1,
   * `sessionCode` được BE ưu tiên cao nhất nếu có mặt, nên gửi giá trị mock này sẽ khiến BE tra cứu
   * nhầm kỳ (rất có thể trả INVENTORY_SESSION_NOT_FOUND). Chỉ gửi `inventoryYearMonth` (giá trị thật,
   * đúng định dạng BE chấp nhận) + `storeCode`.
   */
  getPreview: async (payload: TInventoryReportPreviewReq) => {
    const response: TAxiosResponse<
      TInventoryReportPreviewResponse,
      TCommonError
    > = await axiosInstance.get('/api/v1/reports/inventory/preview', {
      params: {
        inventoryYearMonth: payload.inventoryYearMonth || undefined,
        storeCode: payload.storeCode,
        companyCode: payload.companyCode || undefined,
        page: payload.page ?? 1,
      },
    });
    return response;
  },

  /**
   * POST /api/v1/reports/inventory/pdf — [D5] OK, sinh PDF nhị phân.
   * Status: ĐÃ CÓ ở BE (InventoryReportController.Pdf).
   *
   * Response THÀNH CÔNG (HTTP 200) là raw binary PDF — KHÔNG bọc Result<T> — khác hoàn toàn pattern
   * JSON envelope thông thường của app (api-integration.md §2.2). Bắt buộc dùng
   * axios `responseType: 'blob'`; `services/axiosInstance.ts` response interceptor trả thẳng
   * `response.data`, nên với responseType 'blob' giá trị resolve chính là Blob PDF.
   *
   * Response LỖI (HTTP 400) vẫn là JSON `Result<T>` — nhưng vì request đã set responseType:'blob',
   * axios KHÔNG tự parse JSON của body lỗi này, `error.data` sẽ là 1 Blob chứa text JSON thay vì object.
   * → tự đọc Blob bằng `.text()` rồi `JSON.parse` để khôi phục envelope lỗi thật, re-throw lại đúng
   * hình dạng `{ status, data }` mà lớp gọi (hook) đã quen xử lý cho các API JSON khác trong app.
   */
  generatePdf: async (payload: TInventoryReportPdfReq): Promise<Blob> => {
    try {
      const response = await axiosInstance.post<Blob>(
        '/api/v1/reports/inventory/pdf',
        {
          inventoryYearMonth: payload.inventoryYearMonth || undefined,
          storeCode: payload.storeCode,
          companyCode: payload.companyCode || undefined,
          outputScope: payload.outputScope,
          requestedByUserName: payload.requestedByUserName,
        },
        { responseType: 'blob' }
      );
      // axiosInstance interceptor đã unwrap response.data — với responseType:'blob', giá trị trả về
      // (đứng tên `response` ở đây) chính là Blob PDF, không phải AxiosResponse<Blob>.
      return response as unknown as Blob;
    } catch (error: any) {
      const rawData = error?.data;
      if (rawData instanceof Blob) {
        let parsedData: unknown = null;
        try {
          const text = await rawData.text();
          parsedData = JSON.parse(text);
        } catch {
          parsedData = null;
        }
        if (parsedData) {
          throw { ...error, data: parsedData };
        }
      }
      throw error;
    }
  },
};

export { inventoryReportApi };
